import React from 'react';
import { useContent } from '../../context/ContentContext';
import { resolveMediaUrl } from '../../utils/api';
import { createWhatsAppLink } from '../../utils/formatters';
import { OpenBoothCardsGallery } from './OpenBoothCardsGallery';
import { motion } from 'motion/react';
import { Store, MessageCircle, Sparkles, CheckCircle2, Calendar, Coffee, Award } from 'lucide-react';

export const OpenBoothSection: React.FC = () => {
  const { data } = useContent();
  const { mobileService, contactSettings } = data;

  const photo = resolveMediaUrl(
    mobileService.openBoothBgImage || mobileService.bgImage || mobileService.truckImage
  );

  const openBoothDesc =
    mobileService.openBoothDescription ||
    'Leton Open Booth adalah layanan pop-up coffee bar dari LetonKu yang siap hadir di berbagai acara spesial, pameran publik, bazaar, pernikahan, dan corporate gathering di seluruh Kota Dumai.';

  const whatsappNumber = contactSettings?.whatsapp || '082168936647';
  const eventWaLink = createWhatsAppLink(
    whatsappNumber,
    'Halo LetonKu, saya tertarik untuk BOOK FOR EVENT / OPEN BOOTH. Bisa minta informasi paket dan ketersediaan tanggal?'
  );

  return (
    <section
      id="leton-open-booth"
      className="relative w-full overflow-hidden py-8 sm:py-14 px-4 sm:px-6 lg:px-8 bg-[#F8F5EF] border-t border-[#E8E0D6] font-body"
    >
      <div className="max-w-7xl mx-auto w-full relative z-10">
        {/* Main Split Showcase Card */}
        <div className="bg-[#FFFFFF] rounded-3xl border border-[#E8E0D6] shadow-[0_8px_30px_rgba(43,29,23,0.06)] overflow-hidden grid grid-cols-1 lg:grid-cols-12 gap-0 items-stretch">
          {/* Details Column (7 cols) */}
          <div className="lg:col-span-7 p-6 sm:p-10 lg:p-12 flex flex-col justify-between order-2 lg:order-1">
            <div>
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#B77945]/12 text-[#B77945] border border-[#B77945]/20 text-xs font-semibold uppercase tracking-wider mb-4">
                <Store className="w-3.5 h-3.5 text-[#B77945]" />
                <span>05 — {mobileService.openBoothSubtitle || 'POP-UP & PUBLIC SPACE'}</span>
              </div>

              {/* Title */}
              <h2 className="font-heading font-bold text-2xl sm:text-4xl text-[#2B1D17] tracking-[-0.02em] leading-tight">
                {mobileService.openBoothTitle || 'LETON OPEN BOOTH'}
              </h2>

              {/* Subtitle / Tagline */}
              <p className="mt-2 text-xs sm:text-sm font-semibold text-[#B77945] uppercase tracking-wider">
                Layanan Coffee Bar Untuk Event &amp; Public Space
              </p>

              {/* Description */}
              <p className="mt-3.5 text-xs sm:text-sm text-[#756B64] leading-relaxed">
                {openBoothDesc}
              </p>

              {/* Service Highlights */}
              <div className="mt-6 pt-5 border-t border-[#E8E0D6] grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3.5 rounded-2xl bg-[#F8F5EF] border border-[#E8E0D6] flex flex-col gap-1.5">
                  <div className="w-7 h-7 rounded-lg bg-[#FFFFFF] border border-[#E8E0D6] text-[#B77945] flex items-center justify-center">
                    <Coffee className="w-4 h-4" />
                  </div>
                  <span className="text-xs font-bold text-[#2B1D17] mt-1">Full Barista &amp; Mesin</span>
                  <span className="text-[11px] text-[#756B64] leading-tight">Espresso bar lengkap dengan racikan barista terlatih.</span>
                </div>

                <div className="p-3.5 rounded-2xl bg-[#F8F5EF] border border-[#E8E0D6] flex flex-col gap-1.5">
                  <div className="w-7 h-7 rounded-lg bg-[#FFFFFF] border border-[#E8E0D6] text-[#B77945] flex items-center justify-center">
                    <Award className="w-4 h-4" />
                  </div>
                  <span className="text-xs font-bold text-[#2B1D17] mt-1">Custom Menu Event</span>
                  <span className="text-[11px] text-[#756B64] leading-tight">Signature coffee, matcha, tea, hingga mocktail segar.</span>
                </div>

                <div className="p-3.5 rounded-2xl bg-[#F8F5EF] border border-[#E8E0D6] flex flex-col gap-1.5">
                  <div className="w-7 h-7 rounded-lg bg-[#FFFFFF] border border-[#E8E0D6] text-[#B77945] flex items-center justify-center">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <span className="text-xs font-bold text-[#2B1D17] mt-1">Indoor &amp; Outdoor</span>
                  <span className="text-[11px] text-[#756B64] leading-tight">Setup fleksibel untuk wedding, bazaar, kantor &amp; festival.</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-7 pt-5 border-t border-[#E8E0D6] flex flex-wrap items-center gap-3">
              <a
                href={eventWaLink}
                target="_blank"
                rel="noopener noreferrer"
                id="openbooth-whatsapp-btn"
                className="px-6 py-3 rounded-xl bg-[#39271F] hover:bg-[#2B1D17] text-white font-semibold text-xs sm:text-sm tracking-wide flex items-center gap-2 shadow-[0_6px_16px_rgba(57,39,31,0.18)] transition-all cursor-pointer"
              >
                <MessageCircle className="w-4 h-4 text-[#B77945]" />
                <span>Book For Event / Konsultasi Booth</span>
              </a>
            </div>
          </div>

          {/* Visual Column (5 cols) */}
          <div className="lg:col-span-5 relative min-h-[260px] sm:min-h-[360px] lg:min-h-full bg-[#F8F5EF] order-1 lg:order-2">
            {photo ? (
              <img
                src={photo}
                alt={mobileService.openBoothTitle || 'LETON OPEN BOOTH'}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  const target = e.currentTarget;
                  if (target.src !== '/assets/leton-coffee-beans.jpg' && !target.src.endsWith('/assets/leton-coffee-beans.jpg')) {
                    target.src = '/assets/leton-coffee-beans.jpg';
                  }
                }}
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-[#39271F] p-8 text-center">
                <Store className="w-16 h-16 opacity-35 mb-2" />
                <span className="text-xs font-semibold uppercase tracking-wider text-[#756B64]">
                  Open Booth Experience
                </span>
              </div>
            )}

            {/* Booking Available Badge */}
            <div className="absolute top-4 left-4">
              <span className="px-3.5 py-1.5 rounded-full bg-[#FFFFFF]/95 backdrop-blur-md text-[#39271F] text-xs font-semibold uppercase tracking-wider shadow-xs flex items-center gap-1.5 border border-[#E8E0D6]">
                <Calendar className="w-3.5 h-3.5 text-[#B77945]" />
                <span>Booking Event Tersedia</span>
              </span>
            </div>
          </div>
        </div>

        {/* Horizontal Photo Slider / Carousel for LETON OPEN BOOTH */}
        {mobileService.galleryImages && mobileService.galleryImages.length > 0 && (
          <OpenBoothCardsGallery
            images={mobileService.galleryImages}
            sectionLabel="LETON OPEN BOOTH"
          />
        )}
      </div>
    </section>
  );
};
